import { ContactValuesField } from '../components/ContactValuesField.js';
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { SelectionActionBar } from '../components/SelectionActionBar.js';
import { togglePageSelection } from '../lib/pageSelection.js';
import { collectPageKeys, applySelectedBatches } from '../lib/bulkSelection.js';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Bot, Calendar, CalendarClock, Clock, Phone, Plus, UserCheck } from 'lucide-react';
import {
  AGENT_VOICES,
  INQUIRY_FILTER_STATUSES,
  INQUIRY_SOURCES,
  INQUIRY_STATUSES,
  INQUIRY_TRANSITIONS,
  aiAgentWriteSchema,
  createInquirySchema,
  type AgentVoice,
  type AiAgent,
  type Inquiry,
  type InquiryStatus,
  type Paginated,
} from '@worki/shared';
import { api, buildQuery } from '../lib/api.js';
import { useForm } from '../lib/useForm.js';
import { useDebounced } from '../lib/useDebounced.js';
import { currentLocale } from '../i18n.js';
import { formatDate, formatDateTime } from '../lib/format.js';
import { Button, EmptyState, ErrorNotice, Select, Spinner, Textarea } from '../components/ui.js';
import { CityInput } from '../components/CityInput.js';
import { CustomerMatchNotice, useCustomerMatch } from '../components/CustomerMatchNotice.js';
import { ConfirmDialog, Modal } from '../components/Modal.js';
import { EditableDetailDialog, type DetailField } from '../components/EditableDetailDialog.js';
import { ItemActions } from '../components/ItemActions.js';
import { usePageSearch } from '../lib/pageSearch.js';
import { useToast } from '../components/Toast.js';
import { cn } from '../lib/cn.js';

/**
 * Status pill colours. The two outcomes that mean work is coming read as
 * success and information; waiting reads as a warning, because it is the one
 * state that asks the owner to do something; a finished enquiry drops to the
 * muted grey every other list uses for "nothing outstanding here".
 */
const STATUS_CLASS: Record<InquiryStatus, string> = {
  pending: 'bg-warning/10 text-warning',
  meeting_scheduled: 'bg-success/10 text-success',
  quote_requested: 'bg-info/10 text-info',
  closed: 'bg-muted text-muted-foreground',
};

/**
 * A meeting is stored as one instant and edited as two fields, because that is
 * how a person books one — a day, then a time on it.
 *
 * The split reads in the browser's own zone, and the join writes back through
 * `new Date(...)`, so a 09:00 typed in Israel is stored as 06:00Z and comes
 * back as 09:00. Building the ISO string by hand would skip the conversion and
 * shift every appointment by the offset.
 */
function splitMeeting(iso: string | null): string {
  if (!iso) return '';
  const at = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${at.getFullYear()}-${pad(at.getMonth() + 1)}-${pad(at.getDate())}T${pad(at.getHours())}:${pad(at.getMinutes())}`;
}

/** A cleared field means no meeting. */
function joinMeeting(local: string | undefined): string | null {
  if (!local) return null;
  const at = new Date(local);
  return Number.isNaN(at.getTime()) ? null : at.toISOString();
}


/**
 * The blank agent form. Hoisted out of the dialog so the effect that fills it
 * can depend on it without being re-run by a fresh object every render.
 */
const EMPTY_AGENT = {
  name: '',
  voice: 'female' as AgentVoice,
  virtualNumber: '',
  forwardTo: '',
  trade: '',
  specialties: '',
  inboundQuestions: '',
  outboundQuestions: '',
  active: true,
};

const PAGE_SIZE = 25;

/**
 * Two tabs, not three. The call log sat between them and answered a question
 * nobody had arrived at this screen to ask — every row on it is already
 * represented here as an enquiry when it mattered. It went back to being its
 * own screen (`/calls`), which is also where the dashboard's call figures have
 * always pointed.
 */
const TABS = ['inquiries', 'agents'] as const;
type Tab = (typeof TABS)[number];

const isTab = (value: string | null): value is Tab => TABS.includes(value as Tab);

/** LEAD-003 — the inquiry list, in the reference's card layout. */
export function InquiriesPage() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const toast = useToast();
  const locale = currentLocale();
  const [params, setParams] = useSearchParams();

  // `?tab=` so the create sheet and the rail can both land on a named tab.
  const [tab, setTab] = useState<Tab>(() => {
    const asked = params.get('tab');
    return isTab(asked) ? asked : 'inquiries';
  });
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<InquiryStatus | ''>('');
  const [page, setPage] = useState(1);
  const [selection, updateSelection] = useState<Set<string>>(new Set());
  const selectionEpoch = useRef(0);
  const selectionReadContext = useRef('');
  const setSelection = (next: Parameters<typeof updateSelection>[0]) => { selectionEpoch.current++; updateSelection(next); };
  const [selectMode, setSelectMode] = useState(false);
  const [viewing, setViewing] = useState<Inquiry | null>(null);
  /* Reading and editing are the same sheet, and which one was asked for is the
     difference between a row that was tapped and the Edit command in its menu. */
  const [editingRow, setEditingRow] = useState(false);
  const [createOpen, setCreateOpen] = useState(params.get('new') === '1');
  const [confirmDelete, setConfirmDelete] = useState(false);

  const longPress = useRef<ReturnType<typeof setTimeout> | null>(null);

  // The create sheet in the bottom bar is just as often opened while already
  // standing here, which changes the search string without remounting.
  useEffect(() => {
    if (params.get('new') === '1') setCreateOpen(true);
  }, [params]);

  /*
   * `?open=<id>` opens one enquiry straight away, which is what makes a meeting
   * on the dashboard a link rather than a hint. The row is fetched by id rather
   * than looked up in the list: the enquiry being pointed at is often on
   * another page of it, or filtered out of it entirely.
   */
  const asked = params.get('open');
  const requested = useQuery({
    queryKey: ['inquiry', asked],
    queryFn: () => api.get<Inquiry>(`/inquiries/${asked ?? ''}`),
    enabled: asked !== null,
  });

  useEffect(() => {
    if (requested.data) setViewing(requested.data);
  }, [requested.data]);

  // Same reason, for the tab: a reader already standing on this screen changes
  // the search string without remounting, so the initial state above never runs
  // a second time.
  useEffect(() => {
    const asked = params.get('tab');
    if (isTab(asked)) setTab(asked);
  }, [params]);

  const debouncedSearch = useDebounced(search, 300);
  selectionReadContext.current = JSON.stringify([search, debouncedSearch, status, tab, page]);
  /* UI-11 — the magnifier moved to the bar, beside the name of the screen. */
  usePageSearch(search, (value) => { selectionEpoch.current++; setSearch(value); }, t('inquiries.searchPlaceholder'));
  useEffect(() => { selectionEpoch.current++; setPage(1); }, [debouncedSearch, status, tab]);
  useEffect(() => () => { selectionEpoch.current++; }, []);

  const list = useQuery({
    queryKey: ['inquiries', { debouncedSearch, status, page }],
    queryFn: () =>
      api.get<Paginated<Inquiry>>(
        `/inquiries${buildQuery({
          q: debouncedSearch,
          status,
          sort: 'createdAt',
          dir: 'desc',
          page,
          pageSize: PAGE_SIZE,
        })}`,
      ),
  });

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['inquiries'] });
    void queryClient.invalidateQueries({ queryKey: ['calendar'] });
    void queryClient.invalidateQueries({ queryKey: ['dashboard'] });
  };
  const invalidate = () => {
    refresh();
    setSelection(new Set());
    setSelectMode(false);
  };

  const convert = useMutation({
    mutationFn: (id: string) => api.post(`/inquiries/${id}/convert`),
    onSuccess: () => {
      toast.success(t('inquiries.converted'));
      invalidate();
    },
    onError: () => toast.error(t('errors.conflict')),
  });

  /*
   * REM-002 — a follow-up made from the enquiry that needs one. The reminder is
   * its own record on its own screen, so this is a second request rather than a
   * field on the enquiry; the enquiry's own name travels with it, because a
   * reminder that says only "call back" is no use in a list of thirty.
   */
  const createReminder = (inquiry: Inquiry, when?: string) => {
    const dueAt = joinMeeting(when);
    if (!dueAt) return;
    void api
      .post('/reminders', {
        title: inquiry.subject?.trim() || inquiry.customer?.name || t('inquiries.one'),
        dueAt,
        source: 'inquiry',
        sourceId: inquiry.id,
      })
      .then(() => {
        toast.success(t('inquiries.reminderCreated'));
        void queryClient.invalidateQueries({ queryKey: ['reminders'] });
        void queryClient.invalidateQueries({ queryKey: ['calendar'] });
        void queryClient.invalidateQueries({ queryKey: ['dashboard'] });
      })
      .catch(() => toast.error(t('errors.unknown')));
  };

  const update = useMutation({
    /* `null` is a value the API reads, not an omission: it is how the dialog
       says the meeting is off. */
    mutationFn: ({ id, body }: { id: string; body: Record<string, string | null> }) =>
      api.patch(`/inquiries/${id}`, body),
    onSuccess: () => {
      toast.success(t('common.saved'));
      invalidate();
    },
    onError: () => toast.error(t('common.error')),
  });

  const selectAllMatching = useMutation({
    mutationFn: async (capture: { epoch: number; context: string; q: string; status: InquiryStatus | '' }) =>
      collectPageKeys(async (page) => {
        if (capture.epoch !== selectionEpoch.current || capture.context !== selectionReadContext.current) throw new Error('Selection cancelled');
        return api.get<Paginated<Inquiry>>(`/inquiries${buildQuery({ q: capture.q, status: capture.status, sort: 'createdAt', dir: 'desc', page, pageSize: 100 })}`);
      }, row => row.id),
    onSuccess: (keys, capture) => {
      if (capture.epoch !== selectionEpoch.current || capture.context !== selectionReadContext.current) return;
      setSelection(new Set(keys)); setSelectMode(keys.length > 0);
    },
    onError: (_error, capture) => {
      if (capture.epoch === selectionEpoch.current && capture.context === selectionReadContext.current) toast.error(t('errors.network'));
    },
  });

  const finishBulk = (result: { ok: number; failed: string[]; attempted: string[] }, success = 'common.saved') => {
    refresh();
    setSelection(current => new Set([...current].filter(id => !result.attempted.includes(id) || result.failed.includes(id))));
    setSelectMode(result.failed.length > 0);
    if (result.failed.length) toast.error(t('common.bulkSomeFailed', { count: result.failed.length }));
    else toast.success(t(success));
  };
  const bulkStatus = useMutation({
    mutationFn: async (next: InquiryStatus) => {
      const attempted = [...selection];
      const result = await applySelectedBatches(attempted, async (ids) => {
        const outcome = await api.post<{ updated: number; failed: string[] }>('/inquiries/bulk/status', { ids, status: next });
        return { ok: outcome.updated, failed: outcome.failed };
      }, 200);
      return { ...result, attempted };
    },
    onSuccess: (result) => finishBulk(result),
  });

  const bulkDelete = useMutation({
    mutationFn: async () => {
      const attempted = [...selection];
      const result = await applySelectedBatches(attempted, async (ids) => {
        const outcome = await api.post<{ deleted: number; failed: string[] }>('/inquiries/bulk/delete', { ids });
        return { ok: outcome.deleted, failed: outcome.failed };
      }, 200);
      return { ...result, attempted };
    },
    onSuccess: (result) => {
      setConfirmDelete(false);
      finishBulk(result, 'common.deleted');
    },
  });

  const toggleSelect = (id: string) =>
    setSelection((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const startLongPress = (id: string) => {
    longPress.current = setTimeout(() => {
      setSelectMode(true);
      setSelection(new Set([id]));
    }, 500);
  };
  const cancelLongPress = () => {
    if (longPress.current) clearTimeout(longPress.current);
  };

  const rows = list.data?.items ?? [];
  const total = list.data?.total ?? 0;

  /*
   * Ordered so the pairs that share a line belong together: who called and on
   * what number, then where it came in from and when, then what it is about and
   * where it stands. `message` takes a line of its own — it is the only field
   * here that is a paragraph rather than a value.
   */
  const detailFields = (inquiry: Inquiry): DetailField[] => [
    { key: 'name', label: t('customer.name'), value: inquiry.customer?.name ?? '—', readOnly: true },
    {
      key: 'phone',
      label: t('customer.phone'),
      value: inquiry.customer?.phone ?? '—',
      dir: 'ltr',
      readOnly: true,
    },
    {
      /*
       * Editable. It is typed by whoever took the call and is wrong as often as
       * any other typed field — a lead logged by hand and later found to have
       * come off the site was stuck saying "manual", and every report counting
       * where work comes from counted it wrong.
       */
      key: 'source',
      label: t('inquiries.source'),
      value: inquiry.source,
      type: 'select',
      options: INQUIRY_SOURCES.map((option) => ({
        value: option,
        label: t(`enum.inquirySource.${option}`),
      })),
    },
    {
      key: 'createdAt',
      label: t('inquiries.createdAt'),
      value: formatDate(inquiry.createdAt, locale),
      readOnly: true,
    },
    { key: 'subject', label: t('inquiries.subject'), value: inquiry.subject ?? '' },
    {
      key: 'status',
      label: t('inquiries.status'),
      value: inquiry.status,
      type: 'select',
      // LEAD-002 forbids illegal jumps, so the picker only offers where this
      // inquiry may actually go — plus staying put.
      options: [inquiry.status, ...(INQUIRY_TRANSITIONS[inquiry.status] ?? [])].map((option) => ({
        value: option,
        label: t(`enum.inquiryStatus.${option}`),
      })),
    },
    /*
     * A booked meeting has a time, and until now the status said only that one
     * exists. The pair is always offered rather than appearing when the status
     * is set: the status is edited in this same form, so a field that waited
     * for it would need a second trip through the dialog to be filled in. A
     * time left on an enquiry that ends up somewhere other than "meeting
     * scheduled" is dropped by the API, not kept as a ghost appointment.
     */
    {
      key: 'meetingAt',
      label: t('inquiries.meetingAt'),
      value: splitMeeting(inquiry.meetingAt),
      type: 'datetime-local',
    },
    /*
     * A follow-up, made from the enquiry that needs it. Left blank because it
     * asks rather than reports: reminders are their own records on their own
     * screen, and this is the shortest way from "they want thinking about" to
     * one existing. Filling in the date is what creates it.
     */
    { key: 'reminderAt', label: t('inquiries.reminderAt'), value: '', type: 'datetime-local' },
    { key: 'message', label: t('inquiries.message'), value: inquiry.message ?? '', type: 'textarea', full: true },
  ];

  return (
    <div className="animate-fade-up max-w-3xl mx-auto">
      <div className="flex gap-1 bg-muted rounded-lg p-1 mb-4">
        {TABS.map((id) => (
          <button
            key={id}
            type="button"
            onClick={() => { selectionEpoch.current++; setTab(id); }}
            className={cn(
              'flex-1 py-2 rounded-md text-sm font-medium transition-all',
              tab === id ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground',
            )}
          >
            {t(`inquiries.tabs.${id}`)}
          </button>
        ))}
      </div>

      {tab === 'agents' && <AgentsPanel />}

      {tab === 'inquiries' && (
      <>
      <div className="flex items-center gap-2 mb-4">
        <button
          type="button"
          onClick={() => setCreateOpen(true)}
          className="h-9 px-3 rounded-lg bg-primary text-primary-foreground text-sm font-medium flex items-center gap-2 hover:opacity-90 transition-opacity active:scale-[0.97] shrink-0"
        >
          <Plus className="h-4 w-4" /> {t('shell.new')}
        </button>
      </div>

      {/*
        One filter, not two. Where an enquiry came in from is on every row
        already, as its own chip, and filtering by it answered a question about
        the channel rather than about the work — so the second dropdown was
        removed and this one carries the whole job.

        Its entries are the three states an enquiry can be in while it is still
        the owner's problem. `closed` is reachable, and only reachable, through
        "all": a finished enquiry is something you come across, not something
        you go looking for, and its row still says so in its own badge.
      */}
      <div className="flex items-center gap-2 mb-4 flex-wrap">
        <Select
          value={status}
          aria-label={t('inquiries.status')}
          onChange={(value) => { selectionEpoch.current++; setStatus(value as InquiryStatus | ''); }}
          className="w-auto min-w-40 h-9 bg-muted"
        >
          <option value="">{t('common.allOf', { label: t('inquiries.status') })}</option>
          {INQUIRY_FILTER_STATUSES.map((option) => (
            <option key={option} value={option}>
              {t(`enum.inquiryStatus.${option}`)}
            </option>
          ))}
        </Select>
      </div>

      {selection.size > 0 && (
        <SelectionActionBar count={selection.size} onClear={() => { setSelection(new Set()); setSelectMode(false); }}>
          <button type="button" disabled={selectAllMatching.isPending || bulkStatus.isPending || bulkDelete.isPending} onClick={() => setSelection(new Set(togglePageSelection([...selection], rows.map((row) => row.id))))}>
            {t('common.selectAll')}
          </button>
          <button type="button" disabled={selectAllMatching.isPending || bulkStatus.isPending || bulkDelete.isPending || list.isFetching || search !== debouncedSearch} onClick={() => selectAllMatching.mutate({ epoch: ++selectionEpoch.current, context: selectionReadContext.current, q: debouncedSearch, status })}>
            {t(selectAllMatching.isPending ? 'common.loading' : 'common.selectAllMatching', { count: list.data?.total ?? 0 })}
          </button>
          <Select
            value=""
            aria-label={t('inquiries.bulkStatus')}
            disabled={selectAllMatching.isPending || bulkStatus.isPending || bulkDelete.isPending}
            onChange={(value) => {
              if (value) bulkStatus.mutate(value as InquiryStatus);
            }}
            className="w-auto min-w-36 h-8 px-2 text-xs bg-muted"
          >
            <option value="">{t('inquiries.bulkStatus')}</option>
            {INQUIRY_STATUSES.map((option) => (
              <option key={option} value={option}>
                {t(`enum.inquiryStatus.${option}`)}
              </option>
            ))}
          </Select>
          <button
            type="button"
            onClick={() => setConfirmDelete(true)}
            disabled={selectAllMatching.isPending || bulkStatus.isPending || bulkDelete.isPending}
            className="text-xs text-destructive hover:underline"
          >
            {t('common.delete')}
          </button>
        </SelectionActionBar>
      )}

      <div className="space-y-2">
        {rows.map((inquiry) => (
          <div
            key={inquiry.id}
            role="button"
            tabIndex={0}
            className="glass-card list-tint rounded-lg px-3 py-3 cursor-pointer hover:border-primary/20 transition-colors"
            onClick={() => {
              if (selectMode) return toggleSelect(inquiry.id);
              setEditingRow(false);
              setViewing(inquiry);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') setViewing(inquiry);
            }}
            onMouseDown={() => startLongPress(inquiry.id)}
            onMouseUp={cancelLongPress}
            onMouseLeave={cancelLongPress}
            onTouchStart={() => startLongPress(inquiry.id)}
            onTouchEnd={cancelLongPress}
          >
            <div className="flex items-center gap-2 mb-1">
              {selectMode && (
                <input
                  type="checkbox"
                  checked={selection.has(inquiry.id)}
                  onChange={() => toggleSelect(inquiry.id)}
                  onClick={(e) => e.stopPropagation()}
                  aria-label={t('common.selectRow')}
                  className="rounded border-border h-4 w-4 accent-primary shrink-0"
                />
              )}
              <p className="text-sm font-semibold truncate flex-1">{inquiry.customer?.name ?? '—'}</p>
              <span
                className={cn(
                  'px-2 py-0.5 rounded-full text-[10px] font-medium whitespace-nowrap shrink-0',
                  STATUS_CLASS[inquiry.status],
                )}
              >
                {t(`enum.inquiryStatus.${inquiry.status}`)}
              </span>
            </div>

            {inquiry.subject && <p className="text-xs text-muted-foreground truncate mb-1.5">{inquiry.subject}</p>}

            {/* The one thing the status cannot say by itself: when. */}
            {inquiry.meetingAt && (
              <p className="text-[11px] font-medium text-success mb-1.5 flex items-center gap-1">
                <CalendarClock className="h-3 w-3 shrink-0" aria-hidden />
                <span className="ltr-nums">{formatDateTime(inquiry.meetingAt, locale)}</span>
              </p>
            )}

            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-2 text-[11px] text-muted-foreground min-w-0">
                {inquiry.customer?.phone && (
                  <span className="truncate ltr-nums" dir="ltr">
                    {inquiry.customer.phone}
                  </span>
                )}
                <span>·</span>
                <span className="whitespace-nowrap ltr-nums">{formatDate(inquiry.createdAt, locale)}</span>
                <span className="px-1.5 py-0.5 rounded-full bg-muted text-[10px] shrink-0">
                  {t(`enum.inquirySource.${inquiry.source}`)}
                </span>
              </div>

              <div className="flex items-center gap-1 shrink-0">
                {inquiry.status !== 'meeting_scheduled' && inquiry.status !== 'closed' && (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      convert.mutate(inquiry.id);
                    }}
                    disabled={convert.isPending && convert.variables === inquiry.id}
                    title={t('inquiries.convertToLead')}
                    aria-label={t('inquiries.convertToLead')}
                    className="p-1.5 rounded-md bg-primary/10 hover:bg-primary/20 transition-colors disabled:opacity-50"
                  >
                    <UserCheck className="h-3.5 w-3.5 text-primary" />
                  </button>
                )}
                {inquiry.customer?.phone && (
                  <a
                    href={`tel:${inquiry.customer.phone}`}
                    onClick={(e) => e.stopPropagation()}
                    title={t('calls.redial')}
                    aria-label={t('calls.redial')}
                    className="p-1.5 rounded-md bg-success/10 hover:bg-success/20 transition-colors"
                  >
                    <Phone className="h-3.5 w-3.5 text-success" />
                  </a>
                )}
                <span onClick={(e) => e.stopPropagation()}>
                  {/* No "download" here. An enquiry is four lines of text that
                      are already on the screen, and a .txt file of them is not
                      a document anybody asked for — the row's own menu is for
                      editing it and for removing it. */}
                  <ItemActions
                    onEdit={() => {
                      setEditingRow(true);
                      setViewing(inquiry);
                    }}
                    onDelete={() => {
                      setSelection(new Set([inquiry.id]));
                      setConfirmDelete(true);
                    }}
                  />
                </span>
              </div>
            </div>
          </div>
        ))}

        {list.isLoading && (
          <div className="text-center py-8 text-sm text-muted-foreground">{t('common.loading')}</div>
        )}
        {!list.isLoading && rows.length === 0 && (
          <div className="glass-card rounded-xl text-center py-10 px-4 space-y-2">
            <Calendar className="h-8 w-8 mx-auto text-muted-foreground/50" />
            <p className="text-sm font-medium">{t('inquiries.emptyTitle')}</p>
            <p className="text-xs text-muted-foreground">{t('inquiries.emptyBody')}</p>
            <button
              type="button"
              onClick={() => setCreateOpen(true)}
              className="h-9 px-4 rounded-lg bg-primary text-primary-foreground text-sm font-medium hover:opacity-90 transition-opacity"
            >
              {t('inquiries.new')}
            </button>
          </div>
        )}
      </div>

      {total > PAGE_SIZE && (
        <div className="flex items-center justify-between gap-3 mt-4">
          <button
            type="button"
            disabled={page === 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            className="h-9 px-3 rounded-lg bg-muted text-sm disabled:opacity-40"
          >
            {t('common.previous')}
          </button>
          <span className="text-xs text-muted-foreground ltr-nums">
            {t('common.page')} {page} {t('common.of')} {Math.ceil(total / PAGE_SIZE)}
          </span>
          <button
            type="button"
            disabled={page >= Math.ceil(total / PAGE_SIZE)}
            onClick={() => setPage((p) => p + 1)}
            className="h-9 px-3 rounded-lg bg-muted text-sm disabled:opacity-40"
          >
            {t('common.next')}
          </button>
        </div>
      )}

      <CreateInquiryDialog
        open={createOpen}
        onClose={() => {
          setCreateOpen(false);
          if (params.get('new')) {
            params.delete('new');
            setParams(params, { replace: true });
          }
        }}
        onCreated={invalidate}
      />

      <EditableDetailDialog
        open={Boolean(viewing)}
        onClose={() => {
          setViewing(null);
          setEditingRow(false);
          // Otherwise the effect above would re-open it on the next render.
          if (params.get('open')) {
            const next = new URLSearchParams(params);
            next.delete('open');
            setParams(next, { replace: true });
          }
        }}
        title={t('inquiries.one')}
        startInEdit={editingRow}
        fields={viewing ? detailFields(viewing) : []}
        onSave={(updated) => {
          if (!viewing) return;
          // Only what the API accepts (LEAD-003): the customer card is owned
          // elsewhere and is edited on the customer, not through the enquiry.
          update.mutate({
            id: viewing.id,
            body: {
              subject: updated.subject ?? '',
              message: updated.message ?? '',
              status: updated.status ?? viewing.status,
              source: (updated.source as Inquiry['source'] | undefined) ?? viewing.source,
              meetingAt: joinMeeting(updated.meetingAt),
            },
          });
          // Its own record, so its own request — and it is written after the
          // enquiry so a rejected edit does not leave a reminder about a change
          // that never happened.
          createReminder(viewing, updated.reminderAt);
          setViewing(null);
          setEditingRow(false);
        }}
      />

      <ConfirmDialog
        open={confirmDelete}
        title={t('common.delete')}
        message={t('inquiries.deleteConfirm')}
        loading={bulkDelete.isPending}
        onCancel={() => setConfirmDelete(false)}
        onConfirm={() => bulkDelete.mutate()}
      />
      </>
      )}
    </div>
  );
}

/**
 * LEAD-009 — the phone agents this business has configured.
 *
 * The reference build shows the same list over mock data, so its "new" button
 * writes nothing. Here it writes a row: a name, a voice, the number the agent
 * answers and the questions it asks. Placing calls is still the telephony
 * provider's job — this tab owns the configuration, and nothing on it dials.
 */
function AgentsPanel() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const toast = useToast();

  const [editing, setEditing] = useState<AiAgent | null>(null);
  const [creating, setCreating] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState<AiAgent | null>(null);

  const agents = useQuery({
    queryKey: ['agents'],
    queryFn: () => api.get<{ items: AiAgent[]; numbers: string[] }>('/agents'),
  });

  const refresh = () => queryClient.invalidateQueries({ queryKey: ['agents'] });

  const toggleActive = useMutation({
    mutationFn: ({ id, active }: { id: string; active: boolean }) =>
      api.post(`/agents/${id}/active`, { active }),
    onSuccess: () => void refresh(),
    onError: () => toast.error(t('common.error')),
  });

  const remove = useMutation({
    mutationFn: (id: string) => api.del(`/agents/${id}`),
    onSuccess: () => {
      toast.success(t('common.deleted'));
      setConfirmDelete(null);
      void refresh();
    },
    onError: () => toast.error(t('common.error')),
  });

  if (agents.isLoading) {
    return (
      <div className="py-10 flex justify-center">
        <Spinner />
      </div>
    );
  }
  if (!agents.data) return <ErrorNotice code="errors.unknown" />;

  const { items, numbers } = agents.data;

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-3">
        <Button variant="primary" onClick={() => setCreating(true)}>
          <Plus className="h-4 w-4" />
          {t('inquiries.agentNew')}
        </Button>
        <span className="text-sm font-medium text-muted-foreground">
          {t('inquiries.agentsCount', { count: items.length })}
        </span>
      </div>

      {items.length === 0 ? (
        <EmptyState
          icon={<Bot className="h-8 w-8" />}
          title={t('inquiries.agentsEmpty')}
          body={t('inquiries.agentsEmptyBody')}
        />
      ) : (
        <div className="space-y-2">
          {items.map((agent) => (
            <div key={agent.id} className="glass-card list-tint rounded-xl p-3 flex items-center gap-3">
              <div
                className={cn(
                  'h-9 w-9 rounded-lg grid place-items-center shrink-0',
                  agent.active ? 'bg-success/10 text-success' : 'bg-muted text-muted-foreground',
                )}
              >
                <Bot className="h-4 w-4" />
              </div>

              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{agent.name}</p>
                <p className="text-xs text-muted-foreground truncate">
                  {agent.trade || t('inquiries.agentNoTrades')}
                </p>
                <p className="text-[11px] text-muted-foreground ltr-nums">
                  {agent.virtualNumber || t('inquiries.agentNoNumber')}
                </p>
              </div>

              {/*
                The switch, not a menu entry: turning an agent off is the one
                thing an owner does to it in a hurry, usually because it is
                saying the wrong thing to real callers.
              */}
              <button
                type="button"
                role="switch"
                aria-checked={agent.active}
                aria-label={t('inquiries.agentActive')}
                disabled={toggleActive.isPending}
                onClick={() => toggleActive.mutate({ id: agent.id, active: !agent.active })}
                className={cn(
                  'w-11 h-6 rounded-full shrink-0 relative transition-colors disabled:opacity-50',
                  agent.active ? 'bg-success' : 'bg-muted',
                )}
              >
                <span
                  className={cn(
                    'absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition-all',
                    agent.active ? 'start-[1.375rem]' : 'start-0.5',
                  )}
                />
              </button>

              <ItemActions onEdit={() => setEditing(agent)} onDelete={() => setConfirmDelete(agent)} />
            </div>
          ))}
        </div>
      )}

      <p className="text-xs text-muted-foreground">{t('inquiries.agentsBody')}</p>

      <AgentDialog
        open={creating || editing !== null}
        agent={editing}
        numbers={numbers}
        onClose={() => {
          setCreating(false);
          setEditing(null);
        }}
        onSaved={() => void refresh()}
      />

      <ConfirmDialog
        open={confirmDelete !== null}
        title={t('common.delete')}
        message={t('inquiries.agentDeleteConfirm')}
        loading={remove.isPending}
        onCancel={() => setConfirmDelete(null)}
        onConfirm={() => confirmDelete && remove.mutate(confirmDelete.id)}
      />
    </div>
  );
}

/**
 * The agent form, for both creation and editing — the fields are identical, and
 * two dialogs that differ only in their verb drift apart the moment one gains a
 * field.
 *
 * Only the name is required. An agent is set up over several sittings, and a
 * form that refused to save without a script would have to stay open until the
 * business had written one.
 */
function AgentDialog({
  open,
  agent,
  numbers,
  onClose,
  onSaved,
}: {
  open: boolean;
  agent: AiAgent | null;
  numbers: string[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const { t } = useTranslation();
  const toast = useToast();

  const form = useForm(aiAgentWriteSchema, EMPTY_AGENT);

  // Reset when the dialog opens, not on every render: it stays mounted and only
  // `open` flips, so without this an edit would show whatever the previous one
  // was left holding.
  const { reset } = form;
  useEffect(() => {
    if (!open) return;
    reset(
      agent
        ? {
            name: agent.name,
            voice: agent.voice,
            virtualNumber: agent.virtualNumber ?? '',
            forwardTo: agent.forwardTo ?? '',
            trade: agent.trade ?? '',
            specialties: agent.specialties ?? '',
            inboundQuestions: agent.inboundQuestions ?? '',
            outboundQuestions: agent.outboundQuestions ?? '',
            active: agent.active,
          }
        : EMPTY_AGENT,
    );
  }, [open, agent, reset]);

  const values = form.values as Record<string, unknown>;
  const str = (key: string) => (values[key] as string | undefined) ?? '';

  const submit = () => {
    void form.submit(async (data) => {
      if (agent) await api.put(`/agents/${agent.id}`, data);
      else await api.post('/agents', data);
      toast.success(t('inquiries.agentSaved'));
      onSaved();
      onClose();
    });
  };

  const input = 'w-full h-10 px-3 rounded-lg bg-muted text-sm outline-none placeholder:text-muted-foreground';

  const field = (labelKey: string, node: ReactNode, errorKey?: string) => (
    <div className="min-w-0 space-y-1">
      <label className="text-xs text-muted-foreground">{t(labelKey)}</label>
      {node}
      {errorKey && form.errors[errorKey] && (
        <p className="text-xs text-destructive">{t(form.errors[errorKey])}</p>
      )}
    </div>
  );

  return (
    <Modal
      open={open}
      title={t(agent ? 'inquiries.agentEdit' : 'inquiries.agentNew')}
      onClose={onClose}
      footer={
        <Button variant="primary" onClick={submit} loading={form.submitting} block>
          {t('common.save')}
        </Button>
      }
    >
      {form.formError && <ErrorNotice code={form.formError} />}

      <div className="space-y-3">
        {field(
          'inquiries.agentName',
          <input
            type="text"
            placeholder={t('inquiries.agentNamePlaceholder')}
            aria-label={t('inquiries.agentName')}
            value={str('name')}
            onChange={(e) => form.setValue('name', e.target.value)}
            className={input}
          />,
          'name',
        )}

        {/* Two options, so a segmented control rather than a dropdown: both
            answers are already on screen and each is one tap away. */}
        {field(
          'inquiries.agentVoice',
          <div className="grid grid-cols-2 gap-2" role="radiogroup" aria-label={t('inquiries.agentVoice')}>
            {AGENT_VOICES.map((voice) => (
              <button
                key={voice}
                type="button"
                role="radio"
                aria-checked={values.voice === voice}
                onClick={() => form.setValue('voice', voice)}
                className={cn(
                  'h-10 rounded-lg text-sm font-medium transition-colors',
                  values.voice === voice
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-muted text-muted-foreground hover:bg-muted/70',
                )}
              >
                {t(`enum.agentVoice.${voice}`)}
              </button>
            ))}
          </div>,
        )}

        <div className="grid grid-cols-2 gap-2">
          {field(
            'inquiries.agentNumber',
            <Select
              value={str('virtualNumber')}
              onChange={(value) => form.setValue('virtualNumber', value)}
              placeholder={t('inquiries.agentNumberPick')}
              disabled={numbers.length === 0}
              options={[
                { value: '', label: t('common.none') },
                ...numbers.map((number) => ({ value: number, label: number })),
              ]}
              aria-label={t('inquiries.agentNumber')}
            />,
            'virtualNumber',
          )}
          {field(
            'inquiries.agentForwardTo',
            <input
              type="tel"
              inputMode="tel"
              placeholder={t('inquiries.agentForwardToPlaceholder')}
              aria-label={t('inquiries.agentForwardTo')}
              value={str('forwardTo')}
              onChange={(e) => form.setValue('forwardTo', e.target.value)}
              className={input}
            />,
            'forwardTo',
          )}
        </div>

        {/* A number has to exist before it can be attached, and the screen that
            gets one is not this one. */}
        {numbers.length === 0 && (
          <p className="text-xs text-muted-foreground">{t('inquiries.agentNoNumbers')}</p>
        )}

        <div className="grid grid-cols-2 gap-2">
          {field(
            'inquiries.agentTrade',
            <input
              type="text"
              placeholder={t('inquiries.agentTradePlaceholder')}
              aria-label={t('inquiries.agentTrade')}
              value={str('trade')}
              onChange={(e) => form.setValue('trade', e.target.value)}
              className={input}
            />,
            'trade',
          )}
          {field(
            'inquiries.agentSpecialties',
            <input
              type="text"
              placeholder={t('inquiries.agentSpecialtiesPlaceholder')}
              aria-label={t('inquiries.agentSpecialties')}
              value={str('specialties')}
              onChange={(e) => form.setValue('specialties', e.target.value)}
              className={input}
            />,
            'specialties',
          )}
        </div>

        {field(
          'inquiries.agentInbound',
          <Textarea
            rows={3}
            placeholder={t('inquiries.agentQuestionsPlaceholder')}
            aria-label={t('inquiries.agentInbound')}
            value={str('inboundQuestions')}
            onChange={(e) => form.setValue('inboundQuestions', e.target.value)}
          />,
          'inboundQuestions',
        )}

        {field(
          'inquiries.agentOutbound',
          <Textarea
            rows={3}
            placeholder={t('inquiries.agentQuestionsPlaceholder')}
            aria-label={t('inquiries.agentOutbound')}
            value={str('outboundQuestions')}
            onChange={(e) => form.setValue('outboundQuestions', e.target.value)}
          />,
          'outboundQuestions',
        )}

        <label className="flex items-center justify-between gap-3 pt-1 border-t border-border cursor-pointer">
          <span className="text-sm">{t('inquiries.agentActive')}</span>
          <input
            type="checkbox"
            checked={values.active !== false}
            onChange={(e) => form.setValue('active', e.target.checked)}
            className="h-5 w-5 rounded accent-primary"
          />
        </label>
      </div>
    </Modal>
  );
}
/**
 * LEAD-001 — manual creation, with the customer created inline if new.
 *
 * On `Modal`, and two fields to a line. The overlay this used to own capped
 * itself at `85vh` and scrolled the header along with the body: on a phone,
 * where `vh` is measured against a viewport taller than the visible one and a
 * navigation bar covers the last 4rem of it, eight stacked fields put the
 * create button underneath both. Pairing them removes four rows before any of
 * that matters, and `Modal` handles the rest — safe-area padding, a footer that
 * stays put, focus trapping and Escape.
 */
function CreateInquiryDialog({
  open,
  onClose,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
}) {
  const { t } = useTranslation();
  const toast = useToast();

  const empty = {
    customer: { name: '', phone: '', email: '', city: '' },
    source: 'manual',
    subject: '',
    message: '',
    // Not part of the enquiry payload — the schema drops it on the way out,
    // and it is read straight off the form to make the reminder below.
    reminderAt: '',
  };
  const form = useForm(createInquirySchema, empty);

  const customer = (form.values.customer ?? {}) as { name?: string; phone?: string; email?: string; city?: string; names?: string[]; phones?: string[]; emails?: string[] };
  const setCustomerField = (key: string, value: string) =>
    form.setValue('customer', { ...customer, [key]: value });
  // LEAD-004 — the same question the quote editor asks, for the same reason.
  const match = useCustomerMatch(customer.phone ?? '', customer.name ?? '');

  const submit = () => {
    if (!match.settle()) return;
    void form.submit(async (data) => {
      const inquiry = await api.post<Inquiry>('/inquiries', { ...data, customerMode: match.mode });
      /*
       * REM-002 — the follow-up, if one was asked for. A reminder is its own
       * record on its own screen, so it is a second request; it is made after
       * the enquiry because it needs the enquiry's id to point at, and a
       * reminder that outlived a failed create would point at nothing.
       */
      const dueAt = joinMeeting(String(form.values.reminderAt ?? ''));
      if (dueAt) {
        await api.post('/reminders', {
          title: inquiry.subject?.trim() || inquiry.customer?.name || t('inquiries.one'),
          dueAt,
          source: 'inquiry',
          sourceId: inquiry.id,
        });
      }
      toast.success(t('common.saved'));
      form.reset(empty);
      onCreated();
      onClose();
    });
  };

  const input = 'w-full h-10 px-3 rounded-lg bg-muted text-sm outline-none placeholder:text-muted-foreground';

  /** A field and the error under it, as one cell of the two-column grid. */
  const cell = (node: ReactNode, errorKey?: string) => (
    <div className="min-w-0">
      {node}
      {errorKey && form.errors[errorKey] && (
        <p className="text-xs text-destructive mt-1">{t(form.errors[errorKey])}</p>
      )}
    </div>
  );

  return (
    <Modal
      open={open}
      title={t('inquiries.new')}
      onClose={onClose}
      footer={
        <Button variant="primary" onClick={submit} loading={form.submitting} block>
          {t('common.create')}
        </Button>
      }
    >
      {form.formError && <ErrorNotice code={form.formError} />}

      <div className="grid grid-cols-2 gap-2">
        <ContactValuesField label={t('customer.name')} values={customer.names ?? [customer.name ?? '']} onChange={(names) => form.setValue('customer', { ...customer, names, name: names[0] ?? '' })} />
        <ContactValuesField label={t('customer.phone')} type="tel" values={customer.phones ?? [customer.phone ?? '']} onChange={(phones) => form.setValue('customer', { ...customer, phones, phone: phones[0] ?? '' })} />
        <ContactValuesField label={t('customer.email')} type="email" values={customer.emails ?? [customer.email ?? '']} onChange={(emails) => form.setValue('customer', { ...customer, emails, email: emails[0] ?? '' })} />
        {cell(
          <CityInput
            value={customer.city ?? ''}
            onChange={(value) => setCustomerField('city', value)}
            className={input}
          />,
        )}
        <div className="col-span-2">
          <CustomerMatchNotice state={match} name={customer.name ?? ''} />
        </div>
        {cell(
          <input
            type="text"
            placeholder={t('inquiries.subject')}
            aria-label={t('inquiries.subject')}
            value={String(form.values.subject ?? '')}
            onChange={(e) => form.setValue('subject', e.target.value)}
            className={input}
          />,
        )}
        {cell(
          <Select
            value={String(form.values.source ?? 'manual')}
            aria-label={t('inquiries.source')}
            onChange={(value) => form.setValue('source', value)}
            className="bg-muted"
          >
            {INQUIRY_SOURCES.map((option) => (
              <option key={option} value={option}>
                {t(`enum.inquirySource.${option}`)}
              </option>
            ))}
          </Select>,
        )}
      </div>

      <textarea
        placeholder={t('inquiries.message')}
        aria-label={t('inquiries.message')}
        value={String(form.values.message ?? '')}
        onChange={(e) => form.setValue('message', e.target.value)}
        className="w-full h-16 p-3 rounded-lg bg-muted text-sm outline-none placeholder:text-muted-foreground resize-none"
      />

      {/*
        The follow-up, asked for here rather than remembered and typed again on
        the reminders screen. It stays a reminder record of its own — this only
        saves the walk between the two screens.
      */}
      {cell(
        <input
          type="datetime-local"
          aria-label={t('inquiries.reminderAt')}
          value={String(form.values.reminderAt ?? '')}
          onChange={(e) => form.setValue('reminderAt', e.target.value)}
          className={input}
        />,
      )}
      <p className="text-[11px] text-muted-foreground flex items-center gap-1">
        <Clock className="h-3 w-3" /> {t('inquiries.reminderHint')}
      </p>
    </Modal>
  );
}
